import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type ApiClientProject } from "../lib/api";
import { useClientAuth } from "../lib/clientAuth";
import { Skeleton } from "../components/Loading";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; projects: ApiClientProject[] }
  | { status: "error" };

// Aggregate view of every project the signed-in invitee can see. Tiles
// link to /c/projects/:id which mounts the same ReviewSurface a guest
// reviewer would see, just with the invitee's identity pre-filled.

export function ClientDashboardPage() {
  const { state } = useClientAuth();
  const [load, setLoad] = useState<LoadState>({ status: "loading" });

  const reload = useCallback(async () => {
    setLoad({ status: "loading" });
    try {
      const res = await api.clientListProjects();
      setLoad({ status: "ready", projects: res.projects });
    } catch {
      setLoad({ status: "error" });
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const email = state.status === "authed" ? state.me.email : "";
  const name = state.status === "authed" ? state.me.name : null;

  return (
    <section className="mx-auto max-w-5xl px-6 py-12">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Your reviews</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Signed in as {name ? `${name} · ` : ""}
          <span className="font-mono">{email}</span>
        </p>
      </div>

      <div className="mt-10">
        {load.status === "loading" ? (
          <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <li
                key={i}
                className="rounded-xl border border-neutral-200 bg-white p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-16" rounded="full" />
                </div>
                <Skeleton className="mt-3 h-3 w-44" />
                <Skeleton className="mt-5 h-3 w-24" />
              </li>
            ))}
          </ul>
        ) : load.status === "error" ? (
          <p className="text-sm text-red-600">Couldn’t load your projects.</p>
        ) : load.projects.length === 0 ? (
          <div className="rounded-lg border border-dashed border-neutral-300 p-12 text-center text-neutral-500">
            <p>You don’t have any active reviews right now.</p>
            <p className="mt-2 text-xs">
              When Avista invites you to a project, it’ll show up here.
            </p>
          </div>
        ) : (
          <>
            <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {load.projects.map((p) => (
                <li key={p.id}>
                  <Link
                    to={`/c/projects/${p.id}`}
                    className="group block rounded-xl border border-neutral-200 bg-white p-5 transition hover:border-neutral-300 hover:shadow-md"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h2 className="truncate text-base font-semibold text-neutral-900">
                        {p.projectName}
                      </h2>
                      <OpenCountBadge count={p.openCommentCount} />
                    </div>
                    <p className="mt-2 truncate text-xs text-neutral-500">
                      {shortHost(p.targetUrl)}
                    </p>
                    <p className="mt-4 text-xs font-medium text-neutral-500 group-hover:text-neutral-900">
                      Open review →
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs text-neutral-500">
              Comments you’ve left under this email on any Clarity share
              link are merged in automatically.
            </p>
          </>
        )}
      </div>
    </section>
  );
}

function OpenCountBadge({ count }: { count: number }) {
  if (count === 0) {
    return (
      <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
        All clear
      </span>
    );
  }
  return (
    <span className="shrink-0 rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-700">
      {count} open {count === 1 ? "comment" : "comments"}
    </span>
  );
}

// The full targetUrl is verbose and the share-token subdomains all share
// a long parent (*.workers.dev / *.avista.is) that crowds the card. Render
// just the hostname — and for workers.dev style hosts with a distinctive
// leading subdomain, drop the noisy management suffix entirely.
function shortHost(url: string): string {
  try {
    const host = new URL(url).hostname;
    const labels = host.split(".");
    // 5+ labels almost always means a *.workers.dev style host where the
    // first label is the only one that varies — show that.
    if (labels.length >= 5) return labels[0] ?? host;
    return host;
  } catch {
    return url;
  }
}
